import { useCallback, useRef, useState } from 'react';

import qs from 'qs';
import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useLoginStateAsync } from '#src/app/shared/hooks/session';
import { isLoaded } from '#src/app/shared/utils/async';

import { ReportAbuseConfirmModal } from '../containers/ReportAbuseConfirmModal';
import { ReportAbuseModal } from '../containers/ReportAbuseModal';
import type { ReportContext } from '../shared/types';
import { WIRE_REPORT_TYPE } from '../shared/wireReportType';

export function useOpenReportAbuseModal({
	v2,
	reportContext,
}: {
	v2?: boolean;
	reportContext: ReportContext;
}): UseOpenReportAbuseModalResult {
	const loginState = useLoginStateAsync();
	const location = useLocation();
	const navigate = useNavigate();
	const track = useTracking();

	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	// Captures the element that triggered the modal flow so focus can be explicitly
	// restored after both modals close. React Aria cannot do this automatically because
	// `setIsReportModalOpen(false)` and `setIsConfirmModalOpen(true)` are batched into a
	// single render, causing the confirm modal to open before React Aria has a chance to
	// restore focus to the original trigger.
	const triggerElementRef = useRef<HTMLElement | null>(null);

	const handleReportSuccess = useCallback(() => {
		setIsReportModalOpen(false);
		setIsConfirmModalOpen(true);
	}, []);

	const handleConfirmModalOpenChange = useCallback((isOpen: boolean) => {
		setIsConfirmModalOpen(isOpen);
		if (!isOpen) {
			triggerElementRef.current?.focus();
			triggerElementRef.current = null;
		}
	}, []);

	const open = useCallback((): void => {
		// non-auth users must authenticate before reporting
		// so we're redirecting to the login page, with a redirection back to this page
		if (!isLoaded(loginState) || loginState.value !== 'AUTHENTICATED') {
			const redirectUrl = `/login_or_join?${qs.stringify(
				{ user_flow: 'report_abuse', redirect_to: `${location.pathname}${location.search}` },
				{ encodeValuesOnly: true },
			)}`;
			navigate(redirectUrl);
			return;
		}

		triggerElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

		const { type } = reportContext;
		void track('report_abuse_click', {
			// tracked value must stay wire-stable across `ReportType` renames — see WIRE_REPORT_TYPE
			type: WIRE_REPORT_TYPE[type],
			experiment_variation: v2 ? 'variant' : 'control',
			// This property is used to monitor the v2 experiment
			...(type === 'petition' ? { petition_id: reportContext.petitionId } : {}),
		});
		setIsReportModalOpen(true);
	}, [location, loginState, navigate, reportContext, track, v2]);

	const reportAbuseModals = (
		<>
			<ReportAbuseModal
				isOpen={isReportModalOpen}
				onOpenChange={setIsReportModalOpen}
				onSuccess={handleReportSuccess}
				v2={v2}
				reportContext={reportContext}
			/>
			<ReportAbuseConfirmModal isOpen={isConfirmModalOpen} onOpenChange={handleConfirmModalOpenChange} />
		</>
	);

	// Callers that render their own trigger (rather than going through `ReportAbuseLink`, which
	// lives in the sibling `shared/components/abuse` unit and can't import `WIRE_REPORT_TYPE`
	// directly — components folders may only export components/types) still need a `data-qa` that
	// stays in sync with the wire vocabulary. Computing it here, once, keeps every trigger's
	// `data-qa` derived from the same table instead of re-hardcoded per call site.
	const dataQa = `report-abuse-${WIRE_REPORT_TYPE[reportContext.type]}`;

	return { open, reportAbuseModals, dataQa };
}

type UseOpenReportAbuseModalResult = {
	open: () => void;
	reportAbuseModals: React.ReactNode;
	/** `data-qa` for this report's trigger element, derived from `WIRE_REPORT_TYPE`. */
	dataQa: string;
};
